import { useTranslations } from "next-intl";
import type { ReactNode } from "react";

type ProductReferenceId =
	| "free"
	| "starter"
	| "pro"
	| "ultimate"
	| "enterprise"
	| "lifetime";

/**
 * 定价卡分两组：credits（额度：多少积分、换算成几条视频几张图）和
 * features（能力：分辨率、水印、授权、存储……）。features 第一行是四档共有的工具总括，
 * 其余只写档位之间有差别的项。占位符由 PricingTable 填：
 * __CREDITS__ 换成 config/credits.ts 的额度，__ALLOWANCE__ 换成带 tooltip 的换算行。
 */
export function usePlanData() {
	const t = useTranslations();

	const planData: Record<
		ProductReferenceId,
		{
			title: string;
			credits: ReactNode[];
			features: ReactNode[];
		}
	> = {
		free: {
			title: t("pricing.products.free.title"),
			credits: [
				t("pricing.products.free.credits.credit1"),
				t("pricing.products.free.credits.credit2"),
			],
			features: [
				t("pricing.products.free.features.feature1"),
				t("pricing.products.free.features.feature3"),
				t("pricing.products.free.features.featureVideoNoWatermark"),
				t("pricing.products.free.features.feature4"),
				t("pricing.products.free.features.feature5"),
				t("pricing.products.free.features.feature6"),
				t("pricing.products.free.features.feature7"),
				t("pricing.products.free.features.feature8"),
			],
		},
		starter: {
			title: t("pricing.products.starter.title"),
			credits: [
				t("pricing.products.starter.credits.credit1"),
				t("pricing.products.starter.credits.credit2"),
			],
			features: [
				t("pricing.products.starter.features.feature1"),
				t("pricing.products.starter.features.feature3"),
				t("pricing.products.starter.features.featureLocked"),
				t("pricing.products.starter.features.feature4"),
				t("pricing.products.starter.features.feature5"),
				t("pricing.products.starter.features.feature6"),
				t("pricing.products.starter.features.feature7"),
			],
		},
		pro: {
			title: t("pricing.products.pro.title"),
			credits: [
				t("pricing.products.pro.credits.credit1"),
				t("pricing.products.pro.credits.credit2"),
			],
			features: [
				t("pricing.products.pro.features.feature1"),
				t("pricing.products.pro.features.feature3"),
				t("pricing.products.pro.features.feature4"),
				t("pricing.products.pro.features.feature5"),
				t("pricing.products.pro.features.feature6"),
				t("pricing.products.pro.features.feature7"),
				t("pricing.products.pro.features.feature8"),
			],
		},
		ultimate: {
			title: t("pricing.products.ultimate.title"),
			credits: [
				t("pricing.products.ultimate.credits.credit1"),
				t("pricing.products.ultimate.credits.credit2"),
			],
			features: [
				t("pricing.products.ultimate.features.feature1"),
				t("pricing.products.ultimate.features.feature3"),
				t("pricing.products.ultimate.features.feature4"),
				t("pricing.products.ultimate.features.feature5"),
				t("pricing.products.ultimate.features.feature6"),
				t("pricing.products.ultimate.features.feature7"),
				t("pricing.products.ultimate.features.feature8"),
			],
		},
		enterprise: {
			title: t("pricing.products.enterprise.title"),
			credits: [],
			features: [
				t("pricing.products.enterprise.features.unlimitedProjects"),
				t("pricing.products.enterprise.features.enterpriseSupport"),
			],
		},
		lifetime: {
			title: t("pricing.products.lifetime.title"),
			credits: [],
			features: [
				t("pricing.products.lifetime.features.noRecurringCosts"),
				t("pricing.products.lifetime.features.extendSupport"),
			],
		},
	};

	return { planData };
}
